import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  firestore,
  writeBatch,
  collection,
  doc,
  setDoc,
  getDocs,
  query,
  where
} from '../config/firebaseConfig';
import { Timestamp } from 'firebase/firestore';
import './ImportContactsModal.css';

function ImportQuestoes({ onClose, currentUser }) {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [recordsProcessed, setRecordsProcessed] = useState(0);
  const [recordsIgnored, setRecordsIgnored] = useState(0);
  const [temasMap, setTemasMap] = useState({});
  const [tenantId, setTenantId] = useState(localStorage.getItem('tenantId') || 'unknownTenant');

  // Carregar temas do Firestore e mapear nome para ID
  useEffect(() => {
    const fetchTemas = async () => {
      const temasSnapshot = await getDocs(collection(firestore, 'temas'));
      const temasMap = {};
      temasSnapshot.forEach((doc) => {
        const temaData = doc.data();
        temasMap[temaData.nome.toLowerCase()] = doc.id; // Mapear nome em minúsculas para evitar problemas de case
      });
      setTemasMap(temasMap);
    };

    fetchTemas();
  }, []);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const parseOptions = (item, maxOptions = 5) => {
    const options = [];
    for (let i = 1; i <= maxOptions; i++) {
      const text = item[`option${i}_text`];
      const isCorrect = item[`option${i}_isCorrect`];
      const justificativa = item[`option${i}_justificativa`] || '';
      if (text) {
        options.push({
          text: text,
          isCorrect: isCorrect === 'TRUE' || isCorrect === true,
          justificativa: justificativa
        });
      }
    }
    return options;
  };

  const parseTemas = (temasString) => {
    const temaIds = temasString.split(',').map(tema => {
      const temaId = temasMap[tema.trim().toLowerCase()];
      return temaId || null; // Retorna null se o tema não for encontrado
    }).filter(temaId => temaId); // Remove temas não encontrados
    return temaIds;
  };

  // Verifica duplicidade de enunciado e temas
  const isDuplicate = async (enunciado, temas) => {
    const questoesRef = collection(firestore, 'questoes');
    const q = query(questoesRef, where('enunciado', '==', enunciado), where('temas', 'array-contains-any', temas));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty; // Retorna true se encontrar duplicados
  };

  const handleImport = async () => {
    if (!file) {
      setError('Por favor, selecione um arquivo.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setProgress(0);
    setRecordsProcessed(0);
    setRecordsIgnored(0);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const workbook = XLSX.read(event.target.result, { type: 'binary' });
        const sheetName = 'Questoes'; // Nome da guia a ser lida
        const worksheet = workbook.Sheets[sheetName];
        if (!worksheet) {
          setError('A guia "questoes" não foi encontrada no arquivo.');
          setLoading(false);
          return;
        }

        const data = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
        if (!data || data.length === 0) {
          setError('O arquivo está vazio ou não possui dados válidos.');
          setLoading(false);
          return;
        }

        const batch = writeBatch(firestore);
        const questoesCollectionRef = collection(firestore, 'questoes');
        const logCollectionRef = collection(firestore, 'importLogs');
        const timestamp = Timestamp.now();  // Utilizando Timestamp correto do Firestore
        const user = currentUser ? currentUser.email : 'unknown';

        let recordsInserted = 0;

        for (let i = 0; i < data.length; i++) {
          const item = data[i];
          setRecordsProcessed(i + 1);

          // Validação básica
          if (!item.enunciado) {
            setRecordsIgnored(prev => prev + 1);
            continue;
          }

          // Parse das opções
          const options = parseOptions(item);

          if (options.length === 0) {
            setRecordsIgnored(prev => prev + 1);
            continue;
          }

          // Parse dos temas (agora usando IDs)
          const temas = parseTemas(item.temas || '');

          // Verificar duplicidade antes de inserir
          const duplicate = await isDuplicate(item.enunciado, temas);
          if (duplicate) {
            setRecordsIgnored(prev => prev + 1);
            continue;
          }

          // Conversão da data de expiração (verifica se está no formato YYYY-MM-DD)
          let dataexpiracao = item.dataexpiracao;
          if (dataexpiracao && /^\d{4}-\d{2}-\d{2}$/.test(dataexpiracao)) {
            dataexpiracao += 'T00:00:00Z'; // Adiciona a hora padrão para o formato ISO
          }

          // Estrutura da questão
          const questaoData = {
            bloqueado: item.bloqueado === 'TRUE' || item.bloqueado === true ? true : false,
            createdAt: timestamp,  // Timestamp gerado corretamente
            dataexpiracao: dataexpiracao,
            difficulty: item.difficulty,
            enviado: item.enviado === 'TRUE' || item.enviado === true ? true : false,
            enunciado: item.enunciado,
            temas: temas, // Agora está gravando os IDs dos temas
            options: options,
            tenantId: tenantId, // Adiciona o tenantId ao documento
            updatedAt: timestamp  // Timestamp correto para o update
          };

          // Gerar ID numérico baseado no tempo
          const numericId = Date.now().toString();

          // Definir ID numérico para a questão
          const questaoRef = doc(questoesCollectionRef, numericId);

          batch.set(questaoRef, questaoData);
          recordsInserted += 1;

          // Atualiza o progresso da importação
          setProgress(((i + 1) / data.length) * 100);
        }

        await batch.commit();
        setSuccess('Questões importadas com sucesso!');

        const logData = {
          dataImportacao: timestamp,
          registrosProcessados: data.length,
          registrosInseridos: recordsInserted,
          registrosIgnorados: recordsIgnored,          
          tenantId: tenantId // Grava o tenantId no log de importação
        };

        await setDoc(doc(logCollectionRef, timestamp.toMillis().toString()), logData); // Converte timestamp para string para o ID do log
      } catch (error) {
        console.error(error);
        setError('Erro ao importar questões. Verifique o cadastro de temas.');
      } finally {
        setLoading(false);
      }
    };

    reader.readAsBinaryString(file);
  };

  return (
    <div className="import-contacts-modal">
      <div className="import-contacts-modal-content">
        <h3>Importar Questões</h3>
        <input
          type="file"
          accept=".xlsx"
          onChange={handleFileChange}
        />
        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}
        {loading && (
          <>
            <div className="progress-bar">
              <div className="progress-bar-filled" style={{ width: `${progress}%` }}>
                {Math.round(progress)}%
              </div>
            </div>
            <div className="import-contacts-stats">
              <span>Registros Processados: {recordsProcessed}</span>
              <span>Registros Ignorados: {recordsIgnored}</span>
              <span>Importação: {recordsProcessed} de {recordsProcessed + recordsIgnored}</span>
            </div>
          </>
        )}
        <button onClick={handleImport} disabled={loading}>
          {loading ? 'Importando...' : 'Importar'}
        </button>
        <button onClick={onClose} className="cancel">
          Fechar
        </button>
      </div>
    </div>
  );
}

export default ImportQuestoes;